import React, { useEffect, useCallback } from 'react';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import { ConsolePage } from './containers/pages/ConsolePage';
import { useLocation } from 'react-router';
import { BrowserRouter as Router } from 'react-router';
import * as OIDC from 'services/oidc';
import { useRevokeToken } from 'services/useRevokeToken';
import { withPresentationSupport } from '../services/multiscreen';
import { SidePage } from './containers/pages/SidePage';
import ReactGA from 'react-ga4';

const RoutingLogger = () => {
    const location = useLocation();

    useEffect(() => {
        console.log(`The current URL is ${location.pathname}${location.search}${location.hash}`);
    }, [location]);

    return <></>;
};

type GoogleAnalyticsProps = {
    trackingId?: string;
}

function GoogleAnalytics(props: GoogleAnalyticsProps) {
    const { trackingId } = props;

    // トラッキングID設定
    useEffect(() => {
        if (trackingId === undefined) return;
        ReactGA.initialize(trackingId);
    }, [trackingId]);

    const location = useLocation();
    useEffect(() => {
        if (trackingId === undefined) return;
        ReactGA.send({
            hitType: 'pageview',
            page: location.pathname + location.search,
        });
    }, [trackingId, location.pathname, location.search]);

    return <></>;
}

type WithAuthProps = {
    rootPath: string;
    flow: string;
    logoutUrl: string;
}

const WithAuth = (props: React.PropsWithChildren<WithAuthProps>) => {
    const { rootPath, flow, logoutUrl } = props;
    const revokeToken = useRevokeToken();

    const onSignOut = useCallback(async () => {
        if (revokeToken) {
            await revokeToken();
        }
    }, [revokeToken]);

    return (
        <OIDC.AuthCodeAuthorization rootPath={rootPath} flow={flow} logoutUrl={logoutUrl} onSignOut={onSignOut}>{props.children}</OIDC.AuthCodeAuthorization>
    );
};

function withAuthorizationIfNeeded(rootPath: string, flow: string | undefined, logoutUrl: string) {
    function wrap(node: JSX.Element) {
        return flow
            ? <WithAuth rootPath={rootPath} flow={flow} logoutUrl={logoutUrl}>{node}</WithAuth>
            : node;
    }
    return wrap;
}

const theme = createTheme();

export type AppProps = {
    rootPath: string;
    oidcFlow: string | undefined;
    logoutUrl: string;
    logging: {
        router: boolean;
    };
    trackingId?: string;
}

export const App = (props: AppProps) => {
    const rootPath = props.rootPath;
    const flow = props.oidcFlow;
    const logoutUrl = props.logoutUrl;
    const isRouterLogEnabled = props.logging.router;
    const trackingId = props.trackingId;

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <Router>
                    <GoogleAnalytics trackingId={trackingId} />
                    {
                        isRouterLogEnabled
                            ? <RoutingLogger />
                            : <React.Fragment />
                    }
                    {
                        withPresentationSupport(
                            withAuthorizationIfNeeded(rootPath, flow, logoutUrl)(
                                <ConsolePage rootPath={rootPath} />
                            ),
                            <SidePage />
                        )
                    }
                </Router>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};
